import React, {useState} from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {useGetBatesNext} from '../../../hooks/productions/useProductions';
import '../Demo.scss';

const DemoBatesNext: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionId, setProductionId] = useState<number>(30);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);

  const batesNextQuery = useGetBatesNext(
    {SecurityGroupId: securityGroupId, productionId: productionId},
    isEnabled
  );

  const handleFetch = () => {
    setIsEnabled(true);
  };

  const handleReset = () => {
    setIsEnabled(false);
    setSecurityGroupId(1);
    setProductionId(30);
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🔢 Get Next Bates Numbers API Demo
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Test the get next Bates numbers endpoint for production numbering
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Next Bates Numbers
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/bates/next
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get the next available Bates numbers for production including
            prefix, suffix, and minimum length
          </Typography>
        </CardContent>
      </Card>
      {/* Parameters Section */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Request Parameters
          </Typography>

          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
              sx={{minWidth: 150}}
            />
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
              sx={{minWidth: 150}}
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <Button
              variant="contained"
              onClick={handleFetch}
              disabled={batesNextQuery.isLoading}
              data-testid="demo-bates-next-button">
              {batesNextQuery.isLoading
                ? 'Loading...'
                : 'Fetch Next Bates Numbers'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={batesNextQuery.isLoading}
              data-testid="demo-bates-next-reset-button">
              Reset
            </Button>
          </Box>

          {batesNextQuery.isLoading && (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}>
              <CircularProgress />
            </Box>
          )}

          {batesNextQuery.error && (
            <Alert severity="error" sx={{marginTop: 2}}>
              Error: {batesNextQuery.error.message}
            </Alert>
          )}
        </CardContent>
      </Card>
      {/* Response Data */}
      {batesNextQuery.data && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Next Bates Numbers
            </Typography>

            {batesNextQuery.data.nextBatesNumbers &&
            batesNextQuery.data.nextBatesNumbers.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bates Number</TableCell>
                      <TableCell>Begin Bates</TableCell>
                      <TableCell>Prefix</TableCell>
                      <TableCell>Suffix</TableCell>
                      <TableCell>Min Length</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batesNextQuery.data.nextBatesNumbers.map(
                      (bates: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip
                              label={bates.batesNumber}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{bates.beginBates}</TableCell>
                          <TableCell>
                            <Chip
                              label={bates.prefix || 'None'}
                              color="secondary"
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={bates.suffix || 'None'}
                              color="secondary"
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{bates.minLength}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                No Bates numbers available for the specified parameters.
              </Alert>
            )}

            {/* Raw Response */}
            <Box sx={{marginTop: 3}}>
              <Typography
                variant="h6"
                className="typo-text subhead4_size"
                gutterBottom
              >
                Raw Response
              </Typography>
              <Box
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(batesNextQuery.data, null, 2)}</pre>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      {/* API Details */}
      <Card sx={{marginTop: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            API Details
          </Typography>

          <Box sx={{marginBottom: 2}}>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Request Parameters:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              SecurityGroupId: number (required)
              <br />
              productionId: number (required)
            </Box>
          </Box>

          <Box>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Response Format:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              {`{
  "nextBatesNumbers": [
    {
      "batesNumber": "string",
      "beginBates": "string",
      "suffix": "string",
      "prefix": "string",
      "minLength": "string"
    }
  ]
}`}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DemoBatesNext;
